import { createElement, type ReactNode } from "react";
import { SlotPlaceholder } from "./SlotPlaceholder";
import { htmlAttrsToReactProps } from "./htmlAttrsToReactProps";
import { isKnownSlotId } from "./slotRegistry";

const STRIPPED_TAGS = new Set(["script", "style"]);

/**
 * Convert a host-page `<template>` element's content into an immutable React element tree.
 * Each `<slot name="…">` becomes a `<SlotPlaceholder>` marker; <script> and <style> children
 * are dropped with a console.warn (styling goes through stylesheet-url or inline <style id>).
 * `on*` attributes are stripped for security.
 *
 * The returned tree is built once at widget init and reused across renders.
 */
export const parseTemplate = (
  content: DocumentFragment | Element,
  templateId = "(unknown)",
): ReactNode => {
  const warningCtx: WarningContext = {
    templateId,
    warnedHandlers: new Set(),
    warnedUnknownSlots: new Set(),
  };
  const children = parseChildren(content.childNodes, warningCtx);
  // A DocumentFragment becomes an array of root-level nodes; useful directly as children.
  return children.length === 1 ? children[0] : children;
};

interface WarningContext {
  templateId: string;
  warnedHandlers: Set<string>;
  warnedUnknownSlots: Set<string>;
}

const parseChildren = (
  nodes: NodeListOf<ChildNode>,
  ctx: WarningContext,
): ReactNode[] => {
  const out: ReactNode[] = [];
  for (let i = 0; i < nodes.length; i++) {
    const result = parseNode(nodes[i], i, ctx);
    if (result !== null) out.push(result);
  }
  return out;
};

const parseNode = (
  node: Node,
  index: number,
  ctx: WarningContext,
): ReactNode => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  if (STRIPPED_TAGS.has(tag)) {
    console.warn(
      `[lastbot-chat] <${tag}> inside template "${ctx.templateId}" is not supported and was stripped`,
    );
    return null;
  }

  if (tag === "slot") {
    const name = el.getAttribute("name") ?? "";
    // Dev-mode typo guard: warn (once per unknown id per template) when
    // the slot name doesn't match anything in the v2 registry. Forward-
    // compatible templates declaring future slots will trip this, but
    // the warning is cheap and the false-positive rate is low.
    if (name && !isKnownSlotId(name) && !ctx.warnedUnknownSlots.has(name)) {
      ctx.warnedUnknownSlots.add(name);
      console.warn(
        `[lastbot-chat] template "${ctx.templateId}" references unknown ` +
          `slot id "${name}". Check spelling; see the slot registry for ` +
          `valid ids (or core.listSlots()).`,
      );
    }
    const fallbackChildren = parseChildren(el.childNodes, ctx);
    return createElement(SlotPlaceholder, {
      key: `slot-${name}-${index}`,
      name,
      fallback: fallbackChildren.length > 0 ? fallbackChildren : undefined,
    });
  }

  const { props, droppedHandlers } = htmlAttrsToReactProps(el.attributes);
  for (const handler of droppedHandlers) {
    if (!ctx.warnedHandlers.has(handler)) {
      ctx.warnedHandlers.add(handler);
      console.warn(
        `[lastbot-chat] stripped inline handler "${handler}" inside template "${ctx.templateId}"`,
      );
    }
  }

  const children = parseChildren(el.childNodes, ctx);
  return createElement(
    tag,
    { ...props, key: `${tag}-${index}` },
    ...children,
  );
};
